import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { UserIcon, UserGroupIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '@/services/api';
import { teamApi } from '@/services/teamApi';
import { contentApi, ContentType, AssigneeType } from '@/services/contentApi';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';

interface ReviewPanelProps {
  contentType: ContentType;
  contentId: string;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

/** Mirrors AssignModal's pick-a-team-or-person pattern, but calls requestReview instead of assign. */
const RequestReviewModal: React.FC<{ contentType: ContentType; contentId: string; onClose: () => void }> = ({ contentType, contentId, onClose }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<AssigneeType>('team');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: teamsRes } = useQuery({
    queryKey: ['teams-for-review'],
    queryFn: () => teamApi.getAll({ isActive: true, limit: 100 }),
    enabled: tab === 'team',
  });
  const teams = teamsRes?.data?.data?.teams ?? [];

  const { data: usersRes } = useQuery({
    queryKey: ['users-for-review'],
    queryFn: () => api.get<{ success: boolean; data: { users: Array<{ _id: string; name: string; email: string }> } }>('/users', { params: { limit: 100 } }),
    enabled: tab === 'user',
  });
  const users = usersRes?.data?.data?.users ?? [];

  const requestMutation = useMutation({
    mutationFn: () => contentApi.requestReview(contentType, contentId, tab, selectedId!),
    onSuccess: () => {
      toast.success('Review requested');
      queryClient.invalidateQueries({ queryKey: ['content-review', contentType, contentId] });
      onClose();
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to request review'),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Request Review</h2>

        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            onClick={() => { setTab('team'); setSelectedId(null); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 ${tab === 'team' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            <UserGroupIcon className="h-4 w-4" /> Team
          </button>
          <button
            onClick={() => { setTab('user'); setSelectedId(null); }}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 ${tab === 'user' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            <UserIcon className="h-4 w-4" /> Person
          </button>
        </div>

        <div className="max-h-64 overflow-y-auto space-y-1 mb-6">
          {tab === 'team'
            ? teams.map((team) => (
                <button
                  key={team._id}
                  onClick={() => setSelectedId(team._id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedId === team._id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`}
                >
                  {team.name}
                  <span className="text-xs text-gray-400 ml-1.5">({team.members?.length ?? 0} members)</span>
                </button>
              ))
            : users.map((user) => (
                <button
                  key={user._id}
                  onClick={() => setSelectedId(user._id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm ${selectedId === user._id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`}
                >
                  {user.name}
                  <span className="text-xs text-gray-400 ml-1.5">{user.email}</span>
                </button>
              ))}
          {((tab === 'team' && teams.length === 0) || (tab === 'user' && users.length === 0)) && (
            <p className="text-sm text-gray-400 text-center py-6">Nothing to show here yet.</p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
            Cancel
          </button>
          <button
            onClick={() => requestMutation.mutate()}
            disabled={!selectedId || requestMutation.isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            {requestMutation.isLoading ? 'Requesting…' : 'Request Review'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Drop-in review status + decision widget, same pattern as AssignButton /
 * ContentActivityPanel. Approve/Reject only render when the round is
 * 'pending' AND the logged-in user is plausibly the reviewer (a direct
 * match, or reviewerType is 'team' — team membership itself is only
 * verified server-side, so a non-member clicking Approve/Reject gets a
 * 403 toast rather than a client-side membership check duplicating that
 * logic here).
 */
const ReviewPanel: React.FC<ReviewPanelProps> = ({ contentType, contentId }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const { data: subscription } = useSubscription();
  const hasTeamsFeature = subscription?.entitlements.approvalWorkflows === true;

  const { data } = useQuery({
    queryKey: ['content-review', contentType, contentId],
    queryFn: () => contentApi.getReviewStatus(contentType, contentId),
    enabled: hasTeamsFeature,
  });

  const decideMutation = useMutation({
    mutationFn: ({ reviewId, decision, comment }: { reviewId: string; decision: 'approved' | 'rejected'; comment?: string }) =>
      contentApi.decideReview(contentType, contentId, reviewId, decision, comment),
    onSuccess: (_, { decision }) => {
      toast.success(decision === 'approved' ? 'Approved' : 'Rejected');
      queryClient.invalidateQueries({ queryKey: ['content-review', contentType, contentId] });
      queryClient.invalidateQueries({ queryKey: ['content-activity', contentType, contentId] });
      setRejectingId(null);
      setRejectComment('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to record decision'),
  });

  const current = data?.current ?? null;
  const canDecide = !!current && current.status === 'pending' &&
    (current.reviewerType === 'team' || current.reviewer?.id === user?.id);

  if (subscription && !hasTeamsFeature) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Review</h3>
        {(!current || current.status !== 'pending') && (
          <button
            onClick={() => setShowRequestModal(true)}
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Request Review
          </button>
        )}
      </div>

      {current ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_BADGE[current.status]}`}>{current.status}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
              {current.reviewerType === 'team' ? <UserGroupIcon className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
              {current.reviewer?.name || 'Unknown'}
            </span>
            <span className="text-xs text-gray-400">· requested {formatDistanceToNow(new Date(current.createdAt), { addSuffix: true })}</span>
          </div>
          {current.status !== 'pending' && current.decidedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {current.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">{current.decidedByUser?.name || 'Unknown'}</span>
              {' '}· {formatDistanceToNow(new Date(current.decidedAt), { addSuffix: true })}
            </p>
          )}
          {current.comment && (
            <p className={`text-sm whitespace-pre-wrap ${current.status === 'rejected' ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {current.comment}
            </p>
          )}

          {canDecide && (
            rejectingId === current._id ? (
              <div className="flex gap-2 pt-1">
                <input
                  value={rejectComment}
                  onChange={(e) => setRejectComment(e.target.value)}
                  placeholder="Reason for rejection (optional)…"
                  className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
                />
                <button
                  onClick={() => decideMutation.mutate({ reviewId: current._id, decision: 'rejected', comment: rejectComment.trim() || undefined })}
                  className="px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Confirm
                </button>
                <button onClick={() => { setRejectingId(null); setRejectComment(''); }} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => decideMutation.mutate({ reviewId: current._id, decision: 'approved' })}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                >
                  <CheckCircleIcon className="h-4 w-4" /> Approve
                </button>
                <button
                  onClick={() => setRejectingId(current._id)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 border border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                >
                  <XCircleIcon className="h-4 w-4" /> Reject
                </button>
              </div>
            )
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-400 inline-flex items-center gap-1">
          <ClockIcon className="h-3.5 w-3.5" /> No review requested yet.
        </p>
      )}

      {data?.history && data.history.length > 0 && (
        <details className="pt-1">
          <summary className="text-xs text-gray-400 cursor-pointer">Review history ({data.history.length})</summary>
          <ul className="mt-2 space-y-2">
            {data.history.map((round) => (
              <li key={round._id} className="text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded-full font-medium capitalize ${STATUS_BADGE[round.status]}`}>{round.status}</span>
                  <span>requested from {round.reviewer?.name || 'Unknown'}</span>
                  <span className="text-gray-400">· {formatDistanceToNow(new Date(round.createdAt), { addSuffix: true })}</span>
                </div>
                {round.status !== 'pending' && round.decidedAt && (
                  <div className="mt-0.5">
                    {round.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">{round.decidedByUser?.name || 'Unknown'}</span>
                    {' '}· {formatDistanceToNow(new Date(round.decidedAt), { addSuffix: true })}
                  </div>
                )}
                {round.comment && (
                  <div className={`mt-0.5 italic ${round.status === 'rejected' ? 'text-red-600 dark:text-red-400' : 'text-gray-400'}`}>
                    "{round.comment}"
                  </div>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {showRequestModal && (
        <RequestReviewModal contentType={contentType} contentId={contentId} onClose={() => setShowRequestModal(false)} />
      )}
    </div>
  );
};

export default ReviewPanel;
